import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
// @mui
import { Box } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
// config
import { NAV } from '../../config';
// components
import { useSettingsContext } from '../../components/settings';
import { useAuthContext } from '../../auth/useAuthContext';
//
import Main from './Main';
import Header from './header';
import NavMini from './nav/NavMini';
import NavVertical from './nav/NavVertical';
import NavVerticalAuth from './nav/NavVerticalAuth';

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { themeLayout } = useSettingsContext();
  const { session } = useAuthContext();

  const isDesktop = useResponsive('up', 'lg');

  const [open, setOpen] = useState(false);

  // Force vertical layout for authenticated users to show sidebar
  const isNavHorizontal = !session && themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [sidebarWidth, setSidebarWidth] = useState(NAV.W_DASHBOARD);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    const newWidth = e.clientX;
    if (newWidth >= 200 && newWidth <= 400) {
      setSidebarWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isResizing]);

  const renderNavVertical = session ? (
    <NavVerticalAuth 
      openNav={open} 
      onCloseNav={handleClose}
      width={sidebarWidth}
      isCollapsed={isCollapsed}
      isResizing={isResizing}
      onResizeStart={() => setIsResizing(true)}
    />
  ) : null;

  if (isNavHorizontal) {
    return (
      <Box
        sx={{
          '--sidebar-width': `${isCollapsed ? NAV.W_DASHBOARD_MINI : sidebarWidth}px`,
        }}
      >
        <Header onOpenNav={handleOpen} isCollapsed={isCollapsed} onToggleCollapse={() => setIsCollapsed(!isCollapsed)} session={session} sidebarWidth={sidebarWidth} isResizing={isResizing} />

        {!isDesktop && (session ? renderNavVertical : <NavVertical openNav={open} onCloseNav={handleClose} />)}

        <Main>
          <Outlet />
        </Main>
      </Box>
    );
  }

  if (isNavMini) {
    return (
      <Box
        sx={{
          '--sidebar-width': `${isCollapsed ? NAV.W_DASHBOARD_MINI : sidebarWidth}px`,
        }}
      >
        <Header onOpenNav={handleOpen} isCollapsed={isCollapsed} onToggleCollapse={() => setIsCollapsed(!isCollapsed)} session={session} sidebarWidth={sidebarWidth} isResizing={isResizing} />

        <Box
          sx={{
            display: { lg: 'flex' },
            minHeight: { lg: '100vh' },
          }}
        >
          {isDesktop ? <NavMini /> : renderNavVertical}

          <Main>
            <Outlet />
          </Main>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        '--sidebar-width': `${isCollapsed ? NAV.W_DASHBOARD_MINI : sidebarWidth}px`,
      }}
    >
      <Header onOpenNav={handleOpen} isCollapsed={isCollapsed} onToggleCollapse={() => setIsCollapsed(!isCollapsed)} session={session} sidebarWidth={sidebarWidth} isResizing={isResizing} />

      <Box
        sx={{
          display: { lg: 'flex' },
          minHeight: { lg: '100vh' },
        }}
      >
        {renderNavVertical}

        <Main>
          <Outlet />
        </Main>
      </Box>
    </Box>
  );
}
